import * as Sentry from '@sentry/react';
import {useEffect} from 'react';
import {
  useLocation,
  useNavigationType,
  matchRoutes,
  createRoutesFromChildren,
} from 'react-router';
import {RootState} from '../store';

// Function to configure Sentry. Call this when your app mounts
export const configure = () => {
  Sentry.init({
    dsn: 'https://0dfa4e58c58c4965b2a771a60ffe812b@o76219.ingest.sentry.io/5507134',
    release: process.env.SENTRY_RELEASE,
    environment: process.env.SENTRY_ENV,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        matchRoutes,
        createRoutesFromChildren,
      }),
    ],
    beforeSend(event) {
      // Filter out ApiError - these are expected errors (network issues, 4xx)
      // already handled by useApiClient which displays alerts to the user
      if (event.exception?.values?.some((e) => e.type === 'ApiError')) {
        return null;
      }

      // Filter out non-Error unhandled rejections (Safari noise from extensions)
      // These have no stacktrace and no actionable information
      if (
        event.exception?.values?.some((e) => {
          const isUnhandledRejection =
            e.mechanism?.type === 'onunhandledrejection';
          if (!isUnhandledRejection) return false;

          return (
            e.value?.includes('Object captured as promise rejection') ||
            e.value?.includes('captured as promise rejection')
          );
        })
      ) {
        return null;
      }

      return event;
    },
  });
};

export const createReduxEnhancer = () =>
  Sentry.createReduxEnhancer({
    actionTransformer: (action) => {
      if (action.type === 'user/storeToken') {
        // Return a transformed action to remove sensitive information
        return {
          type: action.type,
          payload: {},
        };
      }

      return action;
    },
    stateTransformer: (state: RootState) => {
      // Transform the state to remove sensitive information
      const transformedState = {
        ...state,
        netatmoWeather: {
          ...state.netatmoWeather,
          mapMarkers: null,
          historyData: null,
          reports: null,
        },
        user: {
          ...state.user,
          token: null,
        },
      };

      return transformedState;
    },
  });

// Function to set user context.
// Use this to send up info about the current logged in user
export const setUser = (email: string, username: string) => {
  if (process.env.NODE_ENV !== 'development') {
    Sentry.setUser({
      id: email,
      username,
      email,
    });
  }
};

// Function to report handled errors to Sentry
// I use this if I want to report some API failure
export const captureErrorMessage = (msg: string, tagCtx = {}) => {
  if (process.env.NODE_ENV !== 'development') {
    Sentry.setTags(tagCtx);
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      Sentry.captureMessage(msg);
    });
  } else {
    console.log(`[error] ${msg}`);
  }
};

export const captureWarningMessage = (msg: string, tagCtx = {}) => {
  if (process.env.NODE_ENV !== 'development') {
    Sentry.setTags(tagCtx);
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      Sentry.captureMessage(msg);
    });
  } else {
    console.log(`[warning] ${msg}`);
  }
};

export const captureInfoMessage = (msg: string, tagCtx = {}) => {
  if (process.env.NODE_ENV !== 'development') {
    Sentry.setTags(tagCtx);
    Sentry.withScope((scope) => {
      scope.setLevel('info');
      Sentry.captureMessage(msg);
    });
  } else {
    console.log(`[info] ${msg}`);
  }
};

export const captureException = () => {
  Sentry.captureException(new Error('Oops!'));
};
